import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { PropsWithChildren } from 'react'

const ITEM_TYPE = 'QUESTION'

type DragItem = {
  index: number
}

type Props = PropsWithChildren<{
  index: number
  onCommentMoved?: (dragItemIndex: number, hoverItemIndex: number) => void
  isDndEnabled?: boolean
}>

const CommentInListDndComponent = ({
  children,
  index,
  onCommentMoved,
  isDndEnabled = true,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  /**
   * Drag
   */
  const [{ isDragging }, drag] = useDrag<
    DragItem,
    unknown,
    { isDragging: boolean }
  >(
    () => ({
      type: ITEM_TYPE,
      item: () => ({ index }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index]
  )

  /**
   * Drop
   */
  const [, drop] = useDrop<DragItem, unknown, never>(
    () => ({
      accept: ITEM_TYPE,
      hover: (item, monitor) => {
        const hoverItemIndex = index
        const dragItemIndex = item.index

        if (!ref.current || hoverItemIndex === dragItemIndex) {
          return
        }

        const mouseCoords = monitor.getClientOffset()

        if (!mouseCoords) {
          return
        }

        const hoverItemBoundingRect = ref.current.getBoundingClientRect()
        const hoverItemMiddleY =
          (hoverItemBoundingRect.bottom - hoverItemBoundingRect.top) / 2
        const dragItemMiddleY = mouseCoords.y - hoverItemBoundingRect.top

        if (
          hoverItemIndex < dragItemIndex &&
          hoverItemMiddleY < dragItemMiddleY
        ) {
          return
        }

        if (
          hoverItemIndex > dragItemIndex &&
          hoverItemMiddleY > dragItemMiddleY
        ) {
          return
        }

        onCommentMoved?.(dragItemIndex, hoverItemIndex)

        // eslint-disable-next-line no-param-reassign
        item.index = hoverItemIndex
      },
    }),
    [index]
  )

  let opacity = 1
  let cursor = 'default'

  if (isDndEnabled) {
    opacity = isDragging ? 0.5 : 0.999
    cursor = isDragging ? 'grabbing' : 'grab'
    drag(drop(ref))
  }

  return (
    <div ref={ref} style={{ opacity, cursor }}>
      {children}
    </div>
  )
}

export default CommentInListDndComponent
